import * as React from "react";
import { Button } from "./button";
import { Card, CardHeader, CardTitle } from "./card";
import { Notice } from "./feedback";
import { SkeletonRows } from "./skeleton";
import { TBody, TEmpty, THead, TableShell } from "./table";

/**
 * The page a cursor-paged list is on.
 *
 * CURSORS, NOT PAGE NUMBERS. The worker pages on the last row's sort key
 * because these lists are written to while they are read -- an offset re-reads
 * a shifting list and the operator sees one order twice and another never. The
 * cursor is opaque by design, so "Next" and "Back" are the only things this
 * component can honestly offer; a page number would be invented.
 *
 * "Back" is a STACK of cursors rather than a reverse query. The worker has no
 * backwards cursor, and keeping the ones already visited is both simpler and
 * exact -- it returns the operator to the page they actually saw.
 *
 * The stack belongs to the `resetKey` it was paged under. A new key -- a
 * filter, a search, a visit -- starts on the first page in the same render,
 * so the first request for it never carries the old list's cursor, and the
 * stack is dropped then too: a key that comes back starts on page one.
 */
export function usePagedCursor(resetKey: string) {
  const [paging, setPaging] = React.useState({ key: resetKey, cursors: [] as string[] });
  if (paging.key !== resetKey) setPaging({ key: resetKey, cursors: [] });
  const cursors = paging.key === resetKey ? paging.cursors : [];
  return {
    cursor: cursors[cursors.length - 1],
    atStart: cursors.length === 0,
    back: () => setPaging((p) => ({ key: p.key, cursors: p.cursors.slice(0, -1) })),
    next: (nextCursor: string) =>
      setPaging((p) => ({ key: p.key, cursors: [...p.cursors, nextCursor] })),
  };
}

/**
 * The cursor Next may follow, or undefined when Next must wait.
 *
 * Only the answer to the page on screen NOW may name the next page, and the
 * answer is that only when the last request finished and did not fail:
 * `useAsync` keeps the previous answer on screen while a page loads and after
 * one fails, and that answer's cursor belongs to the page being left -- spent
 * already, or another filter's list. Following it pages the wrong list, or
 * pushes the same page twice. Every pager here asks this one question.
 */
export function nextCursorFor(state: {
  loading: boolean;
  error: string | null;
  nextCursor: string | undefined;
}): string | undefined {
  return state.loading || state.error ? undefined : state.nextCursor;
}

/**
 * A table of rows, with the three states every list here needs: loading,
 * empty, and full.
 *
 * A list drawn here counts no `TEmpty colSpan` against the `<TH>`s by eye:
 * `columns` is the one number the caller states, and it sizes both.
 */
export function DataTable<T>({
  head,
  rows,
  renderRow,
  columns,
  loading,
  empty,
  title,
}: {
  head: React.ReactNode;
  rows: T[];
  renderRow: (row: T) => React.ReactNode;
  /** For the empty row's colSpan, which has to match or the cell floats. */
  columns: number;
  loading: boolean;
  empty: string;
  /** A heading inside the card, for a screen with more than one table. */
  title?: string;
}) {
  return (
    <Card>
      {title && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      {/* The skeleton shows only when there is nothing yet. A reload with
          rows on screen keeps them: blanking the table loses the row the
          operator was reading because they changed a filter. */}
      {loading && rows.length === 0 ? (
        <SkeletonRows rows={6} cols={Math.min(columns, 6)} />
      ) : (
        <TableShell>
          <THead>{head}</THead>
          <TBody>
            {rows.length === 0 && <TEmpty colSpan={columns}>{empty}</TEmpty>}
            {rows.map(renderRow)}
          </TBody>
        </TableShell>
      )}
    </Card>
  );
}

/** A cursor-paged table: `DataTable`, its error, and Back and Next. */
export function PagedList<T>({
  head,
  rows,
  renderRow,
  columns,
  loading,
  error,
  empty,
  pager,
  nextCursor,
}: {
  head: React.ReactNode;
  rows: T[];
  renderRow: (row: T) => React.ReactNode;
  columns: number;
  loading: boolean;
  error: string | null;
  empty: string;
  pager: ReturnType<typeof usePagedCursor>;
  nextCursor?: string;
}) {
  return (
    <>
      {error && <Notice tone="error">{error}</Notice>}
      <DataTable
        head={head}
        rows={rows}
        renderRow={renderRow}
        columns={columns}
        loading={loading}
        empty={empty}
      />
      <Pager pager={pager} loading={loading} error={error} nextCursor={nextCursor} />
    </>
  );
}

function Pager({
  pager,
  loading,
  error,
  nextCursor,
}: {
  pager: ReturnType<typeof usePagedCursor>;
  loading: boolean;
  error: string | null;
  nextCursor?: string;
}) {
  const next = nextCursorFor({ loading, error, nextCursor });
  return (
    <div className="flex items-center justify-between">
      <Button size="sm" variant="outline" disabled={pager.atStart} onClick={pager.back}>
        Back
      </Button>
      <Button size="sm" variant="outline" disabled={!next} onClick={() => next && pager.next(next)}>
        Next
      </Button>
    </div>
  );
}
